import { useRef, useEffect, useState } from 'react'

// https://dmitripavlutin.com/react-hooks-stale-closures/
// Multiple renders https://stackoverflow.com/questions/53574614/multiple-calls-to-state-updater-from-usestate-in-component-causes-multiple-re-re

/*
  Problems with hooks state management.
  1) Setting multiple state values in the same async useEffect function will cause multiple renders.
  2) Cannot depend on state being correct directly after a setState executes.
  3) Cannot depend on the state being correct for self referencing updating like state value incrementing.
  4) Stale state problem in component asynchronous callbacks.
  5) Brittle solutions to useCallback where the callback requires state.
  6) If state is changed after a component dismount then a react warning is issued indicating a possible memory leak.

  Solution below
  Advantages.
  1) Performs a setState merge like with class components.
  2) So, (1) above is solved since multiple state value updates can be performed by one setState call.
  3) getState() solves (2) above since it always returns the correct instantaneous state regardless of renders.
  4) Since getState() always returns the correct current state, then self referencing updates are solved.
     For example setState({ incval: getState().incVal + 1 }) will be correct.
  5) getState is stable and also returns the latest correct state. Hence, this is called in the component callbacks and then the callback
     can operate based on the current state instead of the stale closure state.
  6) useCallback problems. In general, if the 2nd parameter to useCallback is [], then the function is built after the first render with the state pointer captured in the
     function's capture at that time. So, if function to useCallback is called after additional state changes, that function will still have state = to the capture pointer state.
     This can be overcome by listing each key used by the function in the second argument array like say [state.key1, state.key2]. Then whenever these state keys change, the
     function will be regenerated with a new capture of the state pointer. However, assume a junior developer comes along later and adds code that requires state at a new key, say key3.
     If that keys is not added to the current keylist, then the code will be broken. This is brittle code because of a spagetti connection from the code to the state list.
     However, by using const state = getState() of useMergeState at the top of the callback function, then any newly added state code will have its new state correctly picked up in 
     the state value since getState() always returns the correct current state for the component. Hence, the spagetti connection is removed thereby reducing the possibility of
     bugs. 
  7) Has built in mechanism to prevent setting state when the component is unmounted.
*/

/**
 * @function
 * @description Equivalent to react class component setState method.
 * @param {object} initialState Initial state desired.
 * @returns {Array<object, Function, Function>} {current state, setState, getState}
 */
const useMergeState = initialState => {
  const mounted = useRef(true)
  const currentState = useRef(initialState)
  const callbacks = useRef([])

  const [, setFakeState] = useState(0)
  const fakeState = useRef(0)

  // This is used to cause a component render.
  const forceRender = () => {
    ++fakeState.current
    setFakeState(fakeState.current)
  }
  
  // This will protect against setting state on an unmounted component.
  useEffect(() => {
    return () => mounted.current = false
  }, [])

  // Runs on any state change after the render.
  // Any callbacks sent into setState are run below.
  useEffect(() => {
    if (callbacks.current.length === 0) {
      return
    }
    callbacks.current.forEach(callback => callback(currentState.current))
    callbacks.current = []
  }, [fakeState])

  // The purpose of getState is to solve the stale state closure for callback functions. In this case,
  // when the callback is defined by the interpreter, it captures in its closure the state pointer at that time. So, when the callback is execute, it will
  // have that initial state pointer when referencing the state. So, do const state = getState() inside the callback and this will return the current state
  // instead of the closure state.
  const getState = () =>
    currentState.current

  // The callback argument to setState below functions like the class component setState callback. When the state has been changed for this setState,
  // and the corresponding render has been performed, the callback is executed with the current state as a parameter.
  // On the other hand, to acquire the correct state after the setState and before the render, you can use getState().
  // setState will always cause a render regardless of whether the state changes or not.
  const setState = (mergeObj, callback) => {
    // This mounted.current prevents the react warning of changing state on an unmounted component.
    if (mounted.current) {
      // Merge state change.
      currentState.current = { ...currentState.current, ...mergeObj }
      forceRender()
      if (typeof callback === 'function') {
        // FIFO the callbacks.
        callbacks.current.push(callback)
      }
    }
  }

  return [currentState.current, setState, getState]
}

export default useMergeState